/* eslint-disable react-hooks/exhaustive-deps */

import {
    FC,
    MouseEvent,
    KeyboardEvent,
    useContext,
    useEffect,
    useState
} from 'react';
import { useRouter } from 'next/router';
import { Accordion, Icon } from '@blueprint/ui';
import { useDispatch, useSelector } from 'react-redux';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector,
    resetFilters
} from '@zoopla/redux-reducer-filters';
import { SelectPropertyType } from './SelectPropertyType';
import { SelectBuyingOptions } from './SelectBuyingOptions';
import { SelectTypeOfHome } from './SelectTypeOfHome';
import { SelectPropertyStatus } from './SelectPropertyStatus';
import { SelectFeatures } from './SelectFeatures';
import { SelectLettingType } from './SelectLettingType';
import { SelectFurnishings } from './SelectFurnishings';
import { SelectAddedToSite } from './SelectAddedToSite';
import { SelectAvailability } from './SelectAvailability';
import {
    resetSortOrder,
    selectCurrentSortOrder
} from '@zoopla/redux-reducer-sort-order';
import { Keywords } from './Keywords';
import { SearchContext, SearchSource } from '@zoopla/listing-search-context';
import { SearchPageType } from '@zoopla/listing-search-context';
import {
    FiltersAccordionSection,
    FiltersForm,
    FiltersFormSection,
    FiltersHeader,
    Heading,
    LargeHeadingText,
    ModalCloseIconButton,
    ResetLinkButton,
    SmallHeadingText,
    UpdateButton,
    RemoveHeading,
    StickyFiltersFooter
} from './Filters.styles';
import { updateResults } from '@zoopla/listing-search-update-results';
import { SelectSortOrder } from './SelectSortOrder';
import { resetNewHomesFilter } from './utils/resetNewHomesFilter';
import {
    SelectBeds,
    SelectPriceForSale,
    SelectPriceToRent
} from '@zoopla/listing-search/filters/options';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';

enum LayoutVersion {
    Mobile = 'mobile',
    Desktop = 'desktop'
}

export type FiltersProps = {
    closeModal: () => void;
    closeModalAfterUpdate: () => void;
};
const resetButtonText = 'Reset';
const filtersHeadingText = 'Filter your results';
const updateResultsButtonText = 'Update results';

const getAccordianItems = (
    isTravelTimeSearch: boolean,
    isForSaleSearch: boolean,
    searchPageType: SearchPageType,
    userAlertId: string | null,
    currentSearchSource: SearchSource,
    removeFilterSortEnabled: boolean
): AccordionItem[] => {
    const PriceComponent = isForSaleSearch ? (
        <SelectPriceForSale />
    ) : (
        <SelectPriceToRent />
    );

    const ACCORDION_ITEMS = {
        BEDROOMS: {
            title: 'Bedrooms',
            content: [
                <RemoveHeading key={'beds-section'}>
                    <SelectBeds />
                </RemoveHeading>
            ],
            view: LayoutVersion.Mobile,
            isFirstOfType: true
        },
        PRICE: {
            title: 'Price',
            content: [
                <RemoveHeading key={'price-section'}>
                    {PriceComponent}
                </RemoveHeading>
            ],
            view: LayoutVersion.Mobile,
            isFirstOfType: true
        },
        SORT_ORDER: {
            title: 'Sort',
            content: [
                <SelectSortOrder
                    key="sort-order-section"
                    userAlertId={userAlertId}
                    currentSearchSource={currentSearchSource}
                    searchType={searchPageType}
                />
            ]
        },
        ADDED_TO_SITE: {
            title: 'Added to site',
            content: [
                <SelectAddedToSite
                    key="added-to-site-section"
                    searchPageType={searchPageType}
                />
            ]
        },

        PROPERTY_TYPE: {
            title: 'Property type',
            content: [
                <SelectPropertyType
                    key="property-type-section"
                    searchPageType={searchPageType}
                />
            ]
        },
        // for sale
        TYPE_OF_HOME: {
            title: 'New or pre-owned homes',
            content: [<SelectTypeOfHome key="type-of-home-section" />]
        },
        BUYING_OPTIONS: {
            title: 'Buying options',
            content: [<SelectBuyingOptions key="buying-options-section" />]
        },
        MUST_HAVES: {
            title: 'Must-haves',
            content: [<SelectFeatures key="feature-filters" />]
        },
        PROPERTY_STATUS: {
            title: 'Property status',
            content: [<SelectPropertyStatus key="property-status" />]
        },
        // to rent
        AVAILABILITY: {
            title: 'Availability',
            content: [<SelectAvailability key="availability-section" />]
        },
        LETTING_TYPE: {
            title: 'Letting type',
            content: [<SelectLettingType key="letting-type-section" />]
        },
        FURNISHINGS: {
            title: 'Furnishing',
            content: [<SelectFurnishings key="furnishings-section" />]
        },
        FEATURES: {
            title: 'Features',
            content: [<SelectFeatures key="feature-filters" />]
        }
    };
    if (isTravelTimeSearch) {
        return [
            {
                ...ACCORDION_ITEMS.SORT_ORDER,
                view: LayoutVersion.Mobile,
                isFirstOfType: true
            },
            ACCORDION_ITEMS.PROPERTY_TYPE
        ];
    }

    if (isForSaleSearch) {
        const accordionItems = [
            ACCORDION_ITEMS.BEDROOMS,
            ACCORDION_ITEMS.PRICE,
            {
                ...ACCORDION_ITEMS.SORT_ORDER,
                view: LayoutVersion.Mobile,
                isFirstOfType: true
            },
            ACCORDION_ITEMS.ADDED_TO_SITE,
            ACCORDION_ITEMS.PROPERTY_TYPE,
            ACCORDION_ITEMS.TYPE_OF_HOME,
            ACCORDION_ITEMS.BUYING_OPTIONS,
            ACCORDION_ITEMS.MUST_HAVES,
            ACCORDION_ITEMS.PROPERTY_STATUS
        ];

        if (removeFilterSortEnabled) {
            return accordionItems.filter((accordionItem) => {
                return accordionItem.title !== ACCORDION_ITEMS.SORT_ORDER.title;
            });
        }

        return accordionItems;
    }

    const accordionItems = [
        ACCORDION_ITEMS.BEDROOMS,
        ACCORDION_ITEMS.PRICE,
        {
            ...ACCORDION_ITEMS.SORT_ORDER,
            view: LayoutVersion.Mobile,
            isFirstOfType: true
        },
        ACCORDION_ITEMS.ADDED_TO_SITE,
        ACCORDION_ITEMS.AVAILABILITY,
        ACCORDION_ITEMS.PROPERTY_TYPE,
        ACCORDION_ITEMS.LETTING_TYPE,
        ACCORDION_ITEMS.FURNISHINGS,
        ACCORDION_ITEMS.FEATURES
    ];

    if (removeFilterSortEnabled) {
        return accordionItems.filter((accordionItem) => {
            return accordionItem.title !== ACCORDION_ITEMS.SORT_ORDER.title;
        });
    }

    return accordionItems;
};

type AccordionItem = {
    title: string;
    content: JSX.Element[];
    view?: LayoutVersion;
    isFirstOfType?: boolean;
    isLastOfType?: boolean;
};

export const Filters: FC<FiltersProps> = ({
    closeModal,
    closeModalAfterUpdate
}) => {
    const [removeFilterSortEnabled] = useOptimizelyFeatureFlag(
        'sr-1537-remove-filter-sort'
    );

    const reduxDispatch = useDispatch();
    const { state, dispatch } = useContext(SearchContext);
    const router = useRouter();
    const { query, asPath, push } = router;
    const { searchType, userAlertId, currentSearchSource } = state;

    const filters = useSelector(filtersValuesSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);
    const currentSortOrder = useSelector(selectCurrentSortOrder);

    const [accordianItems, setAccordionItems] = useState<AccordionItem[]>([]);

    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';

    useEffect(() => {
        const accordianItems = getAccordianItems(
            isTravelTimeSearch,
            isForSaleSearch,
            searchType,
            userAlertId,
            currentSearchSource,
            removeFilterSortEnabled
        );

        setAccordionItems(accordianItems);
    }, [isTravelTimeSearch, isForSaleSearch]);

    const resetState = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        reduxDispatch(resetFilters(undefined, query));
        reduxDispatch(resetSortOrder());

        resetNewHomesFilter({ asPath, push });
    };

    const updateAndClose = () => {
        dispatch({ type: 'SET_SEARCH_SOURCE', payload: 'facets' });
        if (userAlertId) {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
                payload: true
            });
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: null
            });
        }

        updateResults(
            filters,
            router,
            searchType,
            'facets',
            currentSortOrder,
            userAlertId
        );
        closeModalAfterUpdate();
    };

    const onUpdateResultsClick = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        updateAndClose();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleKeyUp = (e: KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            updateAndClose();
        }
    };

    return (
        <>
            <FiltersForm onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
                <FiltersHeader data-testid="filters-header">
                    <Heading>
                        <SmallHeadingText>
                            {filtersHeadingText}
                        </SmallHeadingText>
                        <LargeHeadingText>
                            {filtersHeadingText}
                        </LargeHeadingText>
                        <ResetLinkButton
                            variant="tertiary"
                            onClick={resetState}
                            data-testid="filters-modal_reset-button"
                        >
                            {resetButtonText}
                        </ResetLinkButton>
                        <ModalCloseIconButton
                            onClick={closeModal}
                            data-testid="filters-modal_close-button_icon-only"
                        >
                            <Icon
                                iconId="close"
                                size="small"
                                color="primary-brand"
                            />
                        </ModalCloseIconButton>
                    </Heading>
                </FiltersHeader>

                <FiltersAccordionSection>
                    <Accordion
                        isBoxed={false}
                        isInsideModal
                        testId="filters-modal-accordion"
                        items={accordianItems}
                    />
                </FiltersAccordionSection>

                <FiltersFormSection>
                    <Keywords />
                </FiltersFormSection>
            </FiltersForm>
            <StickyFiltersFooter>
                <UpdateButton
                    isFullWidth
                    type="button"
                    onClick={onUpdateResultsClick}
                    data-testid="update-results-button"
                >
                    {updateResultsButtonText}
                </UpdateButton>
            </StickyFiltersFooter>
        </>
    );
};
